/**
 * Voice Chat Domain - LiveKit Integration
 * Manages voice chat connection state and audio controls
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { ConnectionError, ConnectionErrorReason, type DisconnectReason } from 'livekit-client';

// TYPES

type VoiceChatConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

interface VoiceChatState {
  // Connection info from server
  serverUrl: string | null;
  participantToken: string | null;
  /**
   * The team room's try with its token: a room that failed for a reason that
   * may pass is tried again under the next (handleTeamRoomError), which the
   * provider makes a new room of. A fresh token starts again from 0.
   */
  teamRoomAttempt: number;

  // State
  connectionState: VoiceChatConnectionState;
  isMuted: boolean;

  /**
   * When true the server has disabled voice chat for all players (script feature).
   * The voice-chat provider should deafen/mute the local participant at the
   * LiveKit level and prevent the user from un-muting.
   */
  serverVoiceChatEnabled: boolean;

  /**
   * When true, all audio output is silenced for this player's session.
   * Set once at join time from OpCodeGlobalAudioMute and never reversed.
   * Takes priority over all other mute/unmute controls (voice chat toggles,
   * server voice-chat control, speaking rounds, etc.).
   * Microphone capture is unaffected.
   */
  globalAudioMuted: boolean;

  /**
   * The player's own choice to hear no one: every remote voice is turned down
   * on this client alone (voice-chat-provider.tsx). Theirs to switch from the
   * menu; the session's silence and a step's outrank it, and the moderator's
   * private call is heard whatever it says (domains/your-voice.ts).
   */
  incomingAudioMuted: boolean;

  // Remote participants' microphone state (identity -> isMicrophoneEnabled)
  // Updated by RemoteParticipantsMicStateSync component inside LiveKitRoom
  remoteParticipantsMicState: Map<string, boolean>;

  // Participants' audio levels (identity -> audioLevel 0-1)
  // Updated by SpeakingParticipantsSync component inside LiveKitRoom
  // A value of 0 or absence means silent; >0 means speaking
  speakingParticipants: Map<string, number>;

  // When each participant last began to speak (identity -> ms since the
  // epoch), for the rail's order: whoever started last is on top. It moves
  // only as someone starts, never while they go on, so two people talking over
  // each other do not trade places with every level.
  lastSpokeAt: Record<string, number>;

  // Actions
  setConnectionInfo: (serverUrl: string, participantToken: string) => void;
  /** The team room tried again with its token, while it has one. */
  retryTeamRoom: () => void;
  setConnectionState: (state: VoiceChatConnectionState) => void;
  toggleMute: () => void;
  setMuted: (muted: boolean) => void;
  /** Called when the server changes voice chat enabled state (OpCodeVoiceChatControl). */
  setServerVoiceChatEnabled: (enabled: boolean) => void;
  /** Called when the session global audio mute flag is received (OpCodeGlobalAudioMute). */
  setGlobalAudioMuted: (muted: boolean) => void;
  /** The player hears their team, or no one. */
  toggleIncomingAudio: () => void;
  updateRemoteParticipantsMicState: (states: Map<string, boolean>) => void;
  updateSpeakingParticipants: (speaking: Map<string, number>) => void;
  /**
   * Gives the player back their own mute, as they had it — the end of a
   * moderator's call does — and keeps it for their next visit, as the menu's
   * switch does.
   */
  restoreMute: (muted: boolean) => void;
  disconnect: () => void;
  reset: () => void;
}

// STORE

/**
 * `lastSpokeAt` after a new set of levels: whoever is heard now and was not
 * heard in the last set has just begun, and is stamped `now`. Someone still
 * talking keeps the stamp they began with. The same object comes back when no
 * one began, so its subscribers do not re-render.
 */
export function noteSpeakers(
  lastSpokeAt: Record<string, number>,
  previous: Map<string, number>,
  levels: Map<string, number>,
  now: number,
): Record<string, number> {
  let next = lastSpokeAt;
  for (const [identity, level] of levels) {
    if (level <= 0 || (previous.get(identity) ?? 0) > 0) continue;
    if (next === lastSpokeAt) next = { ...lastSpokeAt };
    next[identity] = now;
  }
  return next;
}

/**
 * Where the player's own mute is kept between visits, under a key spelled
 * here alone: the gallery's voice scenes and the tests put it in place
 * through the reader and writer below.
 */
const MUTE_KEY = 'voiceChat_isMuted';

/** The player's own mute, as they last chose it: the menu's switch and a call's end keep it. */
export function storedMute(): boolean {
  return localStorage.getItem(MUTE_KEY) === 'true';
}

/**
 * Keep the player's own mute for their next visit. Storage alone: the
 * store's mute is restoreMute's to change with it.
 */
export function storeMute(muted: boolean): void {
  localStorage.setItem(MUTE_KEY, String(muted));
}

const initialState: Pick<VoiceChatState, 'serverUrl' | 'participantToken' | 'teamRoomAttempt' | 'connectionState' | 'isMuted' | 'serverVoiceChatEnabled' | 'globalAudioMuted' | 'incomingAudioMuted' | 'remoteParticipantsMicState' | 'speakingParticipants' | 'lastSpokeAt'> = {
  serverUrl: null,
  participantToken: null,
  teamRoomAttempt: 0,
  connectionState: 'disconnected',
  isMuted: storedMute(),
  serverVoiceChatEnabled: true,
  globalAudioMuted: false,
  incomingAudioMuted: false,
  remoteParticipantsMicState: new Map<string, boolean>(),
  speakingParticipants: new Map<string, number>(),
  lastSpokeAt: {},
};

export const useVoiceChatStore = create<VoiceChatState>()(
  devtools(
    (set): VoiceChatState => ({
      ...initialState,

      setConnectionInfo: (serverUrl, participantToken) => {
        set(
          {
            serverUrl,
            participantToken,
            teamRoomAttempt: 0,
            connectionState: 'connecting',
          },
          false,
          'setConnectionInfo'
        );
      },

      retryTeamRoom: () => {
        set(
          (state) => (state.participantToken ? { teamRoomAttempt: state.teamRoomAttempt + 1, connectionState: 'connecting' } : state),
          false,
          'retryTeamRoom'
        );
      },

      setConnectionState: (state) => {
        set({ connectionState: state }, false, 'setConnectionState');
      },

      toggleMute: () => {
        set((state) => {
          const newMuted = !state.isMuted;
          storeMute(newMuted);
          return { isMuted: newMuted };
        }, false, 'toggleMute');
      },

      setMuted: (muted) => {
        set({ isMuted: muted }, false, 'setMuted');
      },

      setServerVoiceChatEnabled: (enabled) => {
        set({ serverVoiceChatEnabled: enabled }, false, 'setServerVoiceChatEnabled');
      },

      setGlobalAudioMuted: (muted) => {
        set({ globalAudioMuted: muted }, false, 'setGlobalAudioMuted');
      },

      toggleIncomingAudio: () => {
        set((state) => ({ incomingAudioMuted: !state.incomingAudioMuted }), false, 'toggleIncomingAudio');
      },

      updateRemoteParticipantsMicState: (states) => {
        set({ remoteParticipantsMicState: states }, false, 'updateRemoteParticipantsMicState');
      },

      updateSpeakingParticipants: (speaking) => {
        set(
          (state) => ({
            speakingParticipants: speaking,
            lastSpokeAt: noteSpeakers(state.lastSpokeAt, state.speakingParticipants, speaking, Date.now()),
          }),
          false,
          'updateSpeakingParticipants',
        );
      },

      restoreMute: (muted) => {
        storeMute(muted);
        set({ isMuted: muted }, false, 'restoreMute');
      },

      // The token gone, a try again of its room to come goes too.
      disconnect: () => {
        forgetTeamRoomRetry();
        set(
          {
            serverUrl: null,
            participantToken: null,
            teamRoomAttempt: 0,
            connectionState: 'disconnected',
          },
          false,
          'disconnect'
        );
      },

      // As a fresh page has it: the player's own mute is the one they last
      // chose, not the page's first — they may have changed it since, and a
      // call's end may have given it back.
      reset: () => {
        forgetTeamRoomRetry();
        set({ ...initialState, isMuted: storedMute() }, false, 'reset');
      },
    }),
    { name: 'VoiceChatStore' }
  )
);

// SELECTORS

/**
 * Who is heard now, by identity, in one order. Levels change every 30ms; this
 * changes only as someone begins or stops.
 */
export function speakingIdentities(state: Pick<VoiceChatState, 'speakingParticipants'>): string[] {
  return [...state.speakingParticipants]
    .filter(([, level]) => level > 0)
    .map(([identity]) => identity)
    .sort();
}

/** Who is heard now; re-renders only as someone begins or stops speaking. */
export function useSpeakingIdentities(): string[] {
  return useVoiceChatStore(useShallow(speakingIdentities));
}

// HANDLERS

/** Leave the team room for a private call, keeping the microphone choice to restore. */
export function suspendTeamVoiceForCall(): { teamRoom: boolean; muted: boolean } {
  const { participantToken, isMuted, disconnect, setMuted } = useVoiceChatStore.getState();
  const returnTo = { teamRoom: participantToken !== null, muted: isMuted };
  disconnect();
  setMuted(false);
  return returnTo;
}

/** Give back the player's microphone choice when the moderator's call ends. */
export function restoreVoiceAfterCall(muted: boolean): void {
  useVoiceChatStore.getState().restoreMute(muted);
}

/** Forget voice credentials and session switches as a ban removes the player. */
export function handleVoiceBan(): void {
  useVoiceChatStore.getState().reset();
}

/** Apply the mute chosen by the current floor or session voice setting. */
export function setVoiceMomentMuted(muted: boolean): void {
  useVoiceChatStore.getState().setMuted(muted);
}

/**
 * The game's socket dropped (connection-lost.ts's handleDropped): the team
 * room goes with it, its token and server forgotten, until the rejoin's
 * LiveKit token comes again.
 */
export function handleGameSocketDropped(): void {
  useVoiceChatStore.getState().disconnect();
}

/**
 * Handler for incoming LiveKit token messages from server
 * Called by domain message router when OpCode 4 is received
 */
export function handleLiveKitToken(data: { server_url: string; participant_token: string; room_name: string }): void {
  if (!data.server_url || !data.participant_token || !data.room_name) {
    console.error('[VoiceChat] Invalid token message - missing required fields');
    return;
  }

  console.log('[VoiceChat] A token for the team room', data.room_name);
  // A fresh token starts the room afresh: no try of the old one is still to come.
  forgetTeamRoomRetry();
  const { setConnectionInfo } = useVoiceChatStore.getState();
  setConnectionInfo(data.server_url, data.participant_token);
}

/**
 * Handler for server voice-chat control messages (OpCodeVoiceChatControl = 321).
 * When the server disables voice chat (e.g. during a script step), players are
 * server-muted and cannot un-mute themselves until the server re-enables it.
 */
export function handleVoiceChatControl(data: { enabled: boolean }): void {
  if (typeof data?.enabled !== 'boolean') {
    console.error('[VoiceChat] Invalid voice_chat_control message', data);
    return;
  }
  useVoiceChatStore.getState().setServerVoiceChatEnabled(data.enabled);
}

/**
 * Handler for global audio mute messages (OpCodeGlobalAudioMute = 323).
 * Sets the session-level audio mute flag. Once set, all remote audio output is
 * silenced regardless of any other voice-chat configuration or user action.
 * Microphone capture is not affected.
 */
export function handleGlobalAudioMute(data: { muted: boolean }): void {
  if (typeof data?.muted !== 'boolean') {
    console.error('[VoiceChat] Invalid global_audio_mute message', data);
    return;
  }
  useVoiceChatStore.getState().setGlobalAudioMuted(data.muted);
}

// The team's LiveKit room, as voice-chat-provider.tsx hears of it: what the
// room says (the player's mute as the floor moves is your-voice.ts's
// handleMicMoment). Plain functions, each the same one for the page's whole life — LiveKitRoom
// connects again whenever the onError it is given changes, and the provider
// used to write its callbacks in its render, which ran again on every change
// to this store: a room refused its token was tried again on each, 25 sockets
// in 10 s on a resting page (design-checks: livekit-room-on-error-in-render).
// What that loop did by accident for a room that may come back — the server
// out of reach for a moment — is done on purpose below, a few seconds apart.

/** How long a failed team room waits before each try again, the last held for every try after it. */
export const TEAM_ROOM_RETRY_MS = [2_000, 4_000, 8_000, 16_000, 30_000] as const;

/** The try again to come, if any, and how many tries in a row have failed. */
const teamRoomRetry: { timer: ReturnType<typeof setTimeout> | null; failures: number } = { timer: null, failures: 0 };

/** No try again to come, and the next failure waits the first wait again. */
function forgetTeamRoomRetry(): void {
  if (teamRoomRetry.timer) clearTimeout(teamRoomRetry.timer);
  teamRoomRetry.timer = null;
  teamRoomRetry.failures = 0;
}

/**
 * What a team room does after an error. It tries again a room that may come
 * back with the same token — the server out of reach, its socket failed, the
 * wait ran out, or the connection could not be made; it gives up one that
 * refused the token or sent the player away; and it
 * says nothing of a connection cancelled as its room was left, nor of an
 * error that is not the connection's (the microphone's, which
 * MicrophoneController reports). Every reason LiveKit names is placed, as the
 * portal's moderator windows place them, so one a LiveKit upgrade adds fails
 * the build here.
 */
function teamRoomAfter(error: unknown): 'ignore' | 'retry' | 'giveUp' {
  if (!(error instanceof ConnectionError)) return 'ignore';
  // instanceof gives ConnectionError<any>: its reason is named here, so the switch can place every one.
  const reason: ConnectionErrorReason = error.reason;
  switch (reason) {
    case ConnectionErrorReason.Cancelled:
      return 'ignore';
    case ConnectionErrorReason.ServerUnreachable:
    case ConnectionErrorReason.WebSocket:
    case ConnectionErrorReason.Timeout:
    case ConnectionErrorReason.InternalError:
      return 'retry';
    case ConnectionErrorReason.NotAllowed:
    case ConnectionErrorReason.LeaveRequest:
      return 'giveUp';
    default:
      return unplacedReason(reason);
  }
}

/** A reason this build does not know, at run time: given up, as a refused token is. */
function unplacedReason(_reason: never): 'giveUp' {
  return 'giveUp';
}

/** The team room connected: a try again to come is not needed. */
export function handleTeamRoomConnected(): void {
  console.log('[VoiceChat] Connected to the team room');
  forgetTeamRoomRetry();
  useVoiceChatStore.getState().setConnectionState('connected');
}

/** The team room went: LiveKit gave up on it, or the player left it. */
export function handleTeamRoomDisconnected(reason?: DisconnectReason): void {
  console.log('[VoiceChat] Disconnected from the team room', reason);
  useVoiceChatStore.getState().setConnectionState('disconnected');
}

/**
 * The team room failed. LiveKit's message goes to the log, never the store —
 * nothing read it there; one that may come back is tried again after a wait
 * that doubles with each failure in a row (TEAM_ROOM_RETRY_MS). An error that
 * is not the connection's (the microphone's), or a connection cancelled as its
 * room was left — told after the next room has begun — leaves the state alone.
 */
export function handleTeamRoomError(error: Error): void {
  console.error('[VoiceChat] Error in the team room:', error);
  const after = teamRoomAfter(error);
  if (after === 'ignore') return;
  useVoiceChatStore.getState().setConnectionState('error');
  if (after !== 'retry' || teamRoomRetry.timer) return;
  const wait = TEAM_ROOM_RETRY_MS[Math.min(teamRoomRetry.failures, TEAM_ROOM_RETRY_MS.length - 1)];
  teamRoomRetry.failures += 1;
  teamRoomRetry.timer = setTimeout(() => {
    teamRoomRetry.timer = null;
    useVoiceChatStore.getState().retryTeamRoom();
  }, wait);
}

/** Whose microphones are on among the team, as the room's tracks say (identity → on). */
export function handleRemoteMicStates(states: Map<string, boolean>): void {
  useVoiceChatStore.getState().updateRemoteParticipantsMicState(states);
}

/** Who is heard now, and how loud (identity → level, 0–1), as the room's audio levels say. */
export function handleSpeakingLevels(levels: Map<string, number>): void {
  useVoiceChatStore.getState().updateSpeakingParticipants(levels);
}

// THE PLAYER'S ACTS

/** The player's own microphone switch — their card on the rail, the menu, the moderator's call: kept for their next visit. */
export function toggleMute(): void {
  useVoiceChatStore.getState().toggleMute();
}

/** The player's switch for what they hear of the team, from the menu. */
export function toggleIncomingAudio(): void {
  useVoiceChatStore.getState().toggleIncomingAudio();
}
